import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { isEqual, isEmpty } from 'lodash';
import mediaQueries from 'app/utils/mediaQueries';
import { dlSizeSelectorTracker } from 'app/analytics/pdpEventsAnalytics';
import SizeGuideButton from 'app/productDetails/stickyAddProduct/sizeGuideButton';
import { LEISUREWEAR } from 'app/productDetails/productDetailsConstants';
import SizesList from './sizeSelectorListWrapper';
import SizeGuide from './sizeGuide';
import { isGroupedSizeSelector, isAdultSize, getShoeSizeGuideType } from './sizeSelectorUtils';
import './size-selector.scss';

const { pageType, locale } = window.inlineGlobalConfig;
const isPDP = pageType === 'product';

class SizeSelector extends Component {
  constructor() {
    super();

    this.state = {
      hoveredItem: {},
    };

    this.onSizeSelected = this.onSizeSelected.bind(this);
    this.onHoverSizeItem = this.onHoverSizeItem.bind(this);
    this.getDisplayText = this.getDisplayText.bind(this);
    this.displaySizeSelectorText = this.displaySizeSelectorText.bind(this);
  }

  componentWillUnmount() {
    this.props.onSizeDeselected();
  }

  onHoverSizeItem(hoveredItem) {
    if (!mediaQueries.is_large_up() && window.Modernizr.touchevents) {
      return;
    }
    this.setState({ hoveredItem });
  }

  onSizeSelected(item) {
    const { onSizeSelected, onSizeDeselected, selectedSizeItem, loadedProduct } = this.props;

    if (item && !isEqual(item, selectedSizeItem)) {
      onSizeSelected(item, loadedProduct);
      dlSizeSelectorTracker.dlTrackSizeSelecting({
        dataLayerName: item.dataLayerName,
        errorMessage: item.count <= 0 ? 'sorry this size is out of stock' : '',
      });
    } else {
      onSizeDeselected(selectedSizeItem);
      dlSizeSelectorTracker.dlTrackSizeDeselecting({
        dataLayerName: item.dataLayerName,
      });
    }
  }

  getDisplayText() {
    const { hoveredItem } = this.state;
    const { selectedSizeItem, sizes } = this.props;
    let displayItem = {};

    if (!isEmpty(hoveredItem)) {
      displayItem = hoveredItem;
    } else if (!isEmpty(selectedSizeItem)) {
      displayItem = selectedSizeItem;
    }

    if (isGroupedSizeSelector(sizes) && !isEmpty(displayItem))
      return {
        [isAdultSize(displayItem, locale.country) ? 'adult' : 'kids']:
          displayItem.customLabel || `- ${displayItem.name}`,
      };

    return displayItem.customLabel || displayItem.name;
  }

  displaySizeSelectorText() {
    const { title, sizes } = this.props;
    const displayText = this.getDisplayText();
    if (!isGroupedSizeSelector(sizes)) {
      return (
        <span>
          <FormattedMessage id={title} description="Size" />
          &nbsp;
          {displayText && (
            <span role="alert" title="selected size" className="size-selector__display-text">
              - {displayText}
            </span>
          )}
        </span>
      );
    }
    return null;
  }

  render() {
    const {
      hasStock,
      sizes,
      selectedSizeItem,
      displaySizeGuide,
      allowOutOfStockSizes,
      displaySizeOutOfStockLegend,
      onNotifyAction,
      scrollableList,
      disableNotifyMe,
      productType,
      sizeGuideBtnCta,
      renderSizeGuideBtn,
      loadedProduct,
      silhouette,
      gender,
    } = this.props;

    const isLeisurewearProduct = productType === LEISUREWEAR;
    const shoeSizeGuideType = getShoeSizeGuideType(productType, gender, sizes);

    return (
      <div className="size-selector">
        <div className="size-selector__size">
          {this.displaySizeSelectorText()}
          {displaySizeGuide && (
            <SizeGuide
              silhouette={silhouette}
              gender={gender}
              isLeisurewearProduct={isLeisurewearProduct}
              shoeSizeGuideType={shoeSizeGuideType}
            />
          )}
        </div>
        {isLeisurewearProduct && isPDP && loadedProduct?.modelCopy && (
          <p className="show-for-large-up size-selector__leisurewear-copy">{loadedProduct?.modelCopy}</p>
        )}
        {renderSizeGuideBtn && <SizeGuideButton handleClick={sizeGuideBtnCta} productType={productType} />}
        <SizesList
          getDisplayText={this.getDisplayText}
          country={locale.country}
          sizes={sizes}
          selectedItem={selectedSizeItem}
          onHoverSizeItem={this.onHoverSizeItem}
          onSelectSizeItem={this.onSizeSelected}
          allowOutOfStockSizes={allowOutOfStockSizes}
          scrollableList={scrollableList}
          disableNotifyMe={disableNotifyMe}
          hasStock={hasStock}
          productType={productType}
        />
        {hasStock && displaySizeOutOfStockLegend && !disableNotifyMe && (
          // eslint-disable-next-line
          <a className="product-info__sold-out" onClick={onNotifyAction}>
            <FormattedMessage id="products.details.screen.addToBag.size.soldOutYourSize" />
          </a>
        )}
      </div>
    );
  }
}

SizeSelector.propTypes = {
  sizes: PropTypes.arrayOf(PropTypes.any).isRequired,
  onSizeSelected: PropTypes.func.isRequired,
  onSizeDeselected: PropTypes.func.isRequired,
  hasStock: PropTypes.bool.isRequired,
  title: PropTypes.string,
  selectedSizeItem: PropTypes.object,
  allowOutOfStockSizes: PropTypes.bool,
  loadedProduct: PropTypes.object,
  displaySizeOutOfStockLegend: PropTypes.bool,
  onNotifyAction: PropTypes.func,
  scrollableList: PropTypes.bool,
  displaySizeGuide: PropTypes.bool,
  disableNotifyMe: PropTypes.bool,
  productType: PropTypes.string.isRequired,
  sizeGuideBtnCta: PropTypes.func,
  renderSizeGuideBtn: PropTypes.bool,
};

SizeSelector.defaultProps = {
  title: 'products.pdp.sizeselector.size',
  displaySizeGuide: true,
  loadedProduct: null,
  allowOutOfStockSizes: false,
  displaySizeOutOfStockLegend: true,
  onNotifyAction: () => {},
  scrollableList: false,
  disableNotifyMe: false,
  selectedSizeItem: null,
  sizeGuideBtnCta: () => {},
  renderSizeGuideBtn: false,
};

export default SizeSelector;
